import { z } from 'zod';
import {
  cameraAngleEnum,
  cameraMovementEnum,
  characterExpressionEnum,
  characterPoseEnum,
  colorGradingEnum,
  colorPaletteEnum,
  depthOfFieldEnum,
  focusPointEnum,
  focalLengthEnum,
  foregroundElementEnum,
  frameRateStyleEnum,
  framingGuideEnum,
  horizontalAngleEnum,
  lightDirectionEnum,
  lightQualityEnum,
  lightingRatioEnum,
  lightingSetupEnum,
  locationTypeEnum,
  moodAtmosphereEnum,
  motionIntensityEnum,
  movementMotivationEnum,
  movementSpeedEnum,
  seasonEnum,
  shotPurposeEnum,
  shotSizeEnum,
  subjectMotionEnum,
  subjectPositionEnum,
  subjectTypeEnum,
  timeOfDayEnum,
  eyeDirectionEnum,
  visualEraEnum,
  visualReferenceStyleEnum,
  visualStylePresetEnum,
  weatherConditionEnum
} from './cinematography';

// ============================================================================
// STORY BIBLE SCHEMAS - Global context for visual consistency
// ============================================================================

/** Visual language defines the overall aesthetic of the storyboard */
export const storyBibleVisualLanguageSchema = z.object({
  colorPalette: z.string().max(300), // "Cool blues with warm amber accents"
  lightingStyle: z.string().max(300), // "Natural window light, soft shadows"
  cinematicReference: z.string().max(300), // "Apple commercial meets Fincher"
  era: z.string().max(100) // "Contemporary 2024"
});
export type StoryBibleVisualLanguage = z.infer<typeof storyBibleVisualLanguageSchema>;

/** Character wardrobe with optional scene-specific variations */
export const storyBibleWardrobeSchema = z.object({
  default: z.string().max(500), // "Navy tailored suit, white shirt, no tie"
  variations: z.record(z.string(), z.string().max(500)).optional() // { "scene_3": "Same suit, jacket removed" }
});
export type StoryBibleWardrobe = z.infer<typeof storyBibleWardrobeSchema>;

/** Character definition for visual consistency */
export const storyBibleCharacterSchema = z.object({
  id: z.string(),
  name: z.string().max(100),
  role: z.enum(['protagonist', 'supporting', 'extra']),
  physicalDescription: z.string().max(500), // "30s Middle Eastern man, athletic build, 6ft"
  hairAndGrooming: z.string().max(300), // "Short black hair, clean shaven, well-groomed"
  wardrobe: storyBibleWardrobeSchema,
  props: z.array(z.string().max(100)).optional(), // ["leather briefcase", "smart watch"]
  mannerisms: z.string().max(300).optional() // "Confident posture, deliberate movements"
});
export type StoryBibleCharacter = z.infer<typeof storyBibleCharacterSchema>;

/** Location definition for environmental consistency */
export const storyBibleLocationSchema = z.object({
  id: z.string(),
  name: z.string().max(100), // "Corporate Lobby"
  description: z.string().max(500), // "Modern glass atrium, 3 stories, marble floors"
  timeOfDay: z.string().max(100), // "Morning, 9am"
  weather: z.string().max(200).optional(), // "Overcast, soft diffused light through windows"
  keyElements: z.array(z.string().max(100)) // ["reception desk", "elevator bank", "art installation"]
});
export type StoryBibleLocation = z.infer<typeof storyBibleLocationSchema>;

/** Hero object/product for product-focused storyboards */
export const storyBibleHeroObjectSchema = z.object({
  id: z.string(),
  name: z.string().max(100),
  description: z.string().max(500), // "Matte black smartphone, edge-to-edge display"
  brand: z.string().max(100).optional(),
  importance: z.enum(['hero', 'supporting'])
});
export type StoryBibleHeroObject = z.infer<typeof storyBibleHeroObjectSchema>;

/** Complete Story Bible - global context for the entire storyboard */
export const storyBibleSchema = z.object({
  title: z.string().max(200),
  logline: z.string().max(500), // One-sentence story summary
  genre: z.string().max(100), // "corporate drama", "product showcase"
  tone: z.string().max(100), // "aspirational", "intimate", "epic"
  visualLanguage: storyBibleVisualLanguageSchema,
  characters: z.array(storyBibleCharacterSchema).default([]),
  locations: z.array(storyBibleLocationSchema).default([]),
  heroObjects: z.array(storyBibleHeroObjectSchema).optional()
});
export type StoryBible = z.infer<typeof storyBibleSchema>;

// ============================================================================
// VISUAL BIBLE (CINEMATOGRAPHY CONTROLS)
// ============================================================================

export const visualBibleSchema = z.object({
  stylePreset: visualStylePresetEnum,
  visualEra: visualEraEnum.optional(),
  referenceStyle: visualReferenceStyleEnum.optional(),
  colorPalette: colorPaletteEnum,
  colorGrading: colorGradingEnum.optional(),
  moodAtmosphere: moodAtmosphereEnum,
  lightingSetup: lightingSetupEnum,
  lightQuality: lightQualityEnum.optional(),
  lightDirection: lightDirectionEnum.optional(),
  lightingRatio: lightingRatioEnum.optional(),
  customStyleNotes: z.string().max(500).optional(),
  customColorNotes: z.string().max(500).optional(),
  customLightingNotes: z.string().max(500).optional()
});
export type VisualBible = z.infer<typeof visualBibleSchema>;

// ============================================================================
// APPROVAL + VERSIONING
// ============================================================================

export const storyboardApprovalStatusSchema = z.enum(['pending', 'approved', 'rejected']);
export const storyboardApprovalModeSchema = z.enum(['manual', 'auto']);
export const storyboardApprovalSchema = z.object({
  mode: storyboardApprovalModeSchema.default('manual'),
  status: storyboardApprovalStatusSchema.default('pending'),
  decidedBy: z.string().optional(),
  decidedAt: z.string().datetime().optional(),
  notes: z.string().max(500).optional()
});
export type StoryboardApproval = z.infer<typeof storyboardApprovalSchema>;

// ============================================================================
// CORE ENUMS (shared across schemas)
// ============================================================================

export const storyboardStatusSchema = z.enum([
  'draft',
  'analyzing',
  'analysis_ready',
  'anchor_pending',
  'anchor_ready',
  'scene_generating',
  'video_generating',
  'exporting',
  'ready',
  'error',
  'archived'
]);
export type StoryboardStatus = z.infer<typeof storyboardStatusSchema>;

export const storyboardGenerationModeSchema = z.enum(['auto', 'directed']);
export type StoryboardGenerationMode = z.infer<typeof storyboardGenerationModeSchema>;

export const storyboardAspectRatioSchema = z.enum(['16:9', '9:16', '1:1', '4:3', '21:9']);
export const storyboardResolutionSchema = z.enum(['720p', '1080p', '2k', '4k']);
export const storyboardTransitionSchema = z.enum(['jump_cut', 'last_frame_first_frame']);
export const storyboardSourceSchema = z.enum(['fal_single', 'upload']);
export const storyboardReferenceCategorySchema = z.enum(['character', 'location', 'prop', 'style', 'logo', 'generic']);
export const storyboardReferenceLibraryStatusSchema = z.enum(['active', 'archived', 'hidden']);
export type StoryboardReferenceLibraryStatus = z.infer<typeof storyboardReferenceLibraryStatusSchema>;
export const storyboardModeSchema = z.enum(['fast', 'detailed']);
export type StoryboardMode = z.infer<typeof storyboardModeSchema>;

// ============================================================================
// ENHANCED SCENE SHOT SCHEMAS - Director-level shot specifications
// ============================================================================

/** Character presence in a specific scene */
export const sceneCharacterPresenceSchema = z.object({
  characterId: z.string(),
  wardrobeOverride: z.string().max(500).optional(), // Override default wardrobe if needed
  position: z.string().max(300), // "enters frame left", "seated at desk"
  action: z.string().max(300), // "walks confidently toward reception"
  expression: z.string().max(200), // "subtle determination"
  eyeline: z.string().max(200).optional() // "looking ahead", "glances at phone"
});
export type SceneCharacterPresence = z.infer<typeof sceneCharacterPresenceSchema>;

/** Lighting specifications for a scene */
export const sceneLightingSchema = z.object({
  keyLight: z.string().max(300), // "Soft window light from camera-right"
  practicals: z.string().max(300).optional(), // "Desk lamp, monitor glow"
  mood: z.string().max(200) // "Warm, inviting"
});
export type SceneLighting = z.infer<typeof sceneLightingSchema>;

/** Composition specifications for a scene */
export const sceneCompositionSchema = z.object({
  framing: z.string().max(300), // "Rule of thirds, subject left, negative space right"
  foreground: z.string().max(300).optional(), // "Glass door frame"
  background: z.string().max(300).optional(), // "Blurred office workers"
  depth: z.string().max(300).optional() // "Three layers: desk, subject, window"
});
export type SceneComposition = z.infer<typeof sceneCompositionSchema>;

/** Enhanced scene shot with full director-level specifications */
export const enhancedSceneShotSchema = z.object({
  id: z.string(),
  order: z.number().int().min(0),
  durationSec: z.number().int().min(1).max(30),

  // NARRATIVE
  beatSummary: z.string().max(500), // "Protagonist arrives, establishing his world"
  narrativeFunction: z.enum(['setup', 'rising', 'climax', 'resolution']),

  // LOCATION & TIME
  locationId: z.string(), // References locations array
  timeOfDay: z.string().max(100), // Can override location default
  timeSinceLastScene: z.enum(['continuous', 'moments_later', 'hours_later', 'next_day']).optional(),
  weatherOverride: z.string().max(200).optional(),

  // CHARACTERS IN SCENE
  charactersPresent: z.array(sceneCharacterPresenceSchema).default([]),

  // CINEMATOGRAPHY
  shotType: z.enum([
    'extreme_wide',
    'wide',
    'medium_wide',
    'medium',
    'medium_close_up',
    'close_up',
    'extreme_close_up',
    'insert'
  ]),
  cameraAngle: z.enum(['eye_level', 'low_angle', 'high_angle', 'dutch', 'birds_eye', 'worms_eye']),
  cameraMovement: z.enum([
    'static',
    'pan_left',
    'pan_right',
    'tilt_up',
    'tilt_down',
    'dolly_in',
    'dolly_out',
    'tracking',
    'crane',
    'handheld'
  ]),
  lensDescription: z.string().max(200), // "35mm, medium depth of field"
  focusPoint: z.string().max(200), // "protagonist's face"

  // LIGHTING
  lighting: sceneLightingSchema.optional(),

  // COMPOSITION
  composition: sceneCompositionSchema.optional(),

  // CONTINUITY
  visualBridge: z.string().max(300).optional(), // "Match cut on hand reaching for door"
  matchElements: z.string().max(300).optional(), // "Same wardrobe, same lighting direction"

  // TRANSITION
  transition: z.enum(['cut', 'dissolve', 'match_cut', 'fade']).default('cut'),

  // AUDIO CONTEXT (not rendered in image, but provides context)
  dialogue: z.string().max(500).optional(),
  soundscape: z.string().max(300).optional() // "Ambient lobby noise, footsteps on marble"
});
export type EnhancedSceneShot = z.infer<typeof enhancedSceneShotSchema>;

// ============================================================================
// REFERENCES & MAPPINGS
// ============================================================================

export const storyboardReferenceSourceSchema = z.enum(['user_upload', 'generated_anchor', 'analysis', 'legacy']);
export type StoryboardReferenceSource = z.infer<typeof storyboardReferenceSourceSchema>;

export const storyboardReferenceStatusSchema = z.enum(['pending', 'ready', 'error', 'rejected']);
export type StoryboardReferenceStatus = z.infer<typeof storyboardReferenceStatusSchema>;

export const storyboardReferenceImportanceSchema = z.enum(['required', 'preferred', 'optional']);
export type StoryboardReferenceImportance = z.infer<typeof storyboardReferenceImportanceSchema>;

export const storyboardReferenceVersionSchema = z.object({
  version: z.number().int().positive(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  imageUrl: z.string().url().optional(),
  anchorPrompt: z.string().optional(),
  status: storyboardReferenceStatusSchema.default('pending'),
  sourceType: storyboardSourceSchema.optional(),
  createdAt: z.string().datetime().optional(),
  createdBy: z.string().optional()
});
export type StoryboardReferenceVersion = z.infer<typeof storyboardReferenceVersionSchema>;

export const storyboardReferencePlanSchema = z.object({
  version: z.number().int().positive().default(1),
  userRefs: z
    .array(
      z.object({
        id: z.string(),
        category: storyboardReferenceCategorySchema,
        label: z.string(),
        entityId: z.string()
      })
    )
    .default([]),
  generatedAnchors: z
    .array(
      z.object({
        id: z.string(),
        category: z.enum(['character', 'location', 'prop']),
        label: z.string(),
        entityId: z.string(),
        anchorPrompt: z.string(),
        priority: z.number().int().positive().default(1)
      })
    )
    .default([])
});
export type StoryboardReferencePlan = z.infer<typeof storyboardReferencePlanSchema>;

export const storyboardSceneReferenceMappingSchema = z.object({
  sceneId: z.string(),
  useRefs: z
    .array(
      z.object({
        refId: z.string(),
        category: storyboardReferenceCategorySchema,
        rationale: z.string().max(500),
        importance: storyboardReferenceImportanceSchema
      })
    )
    .default([])
});
export type StoryboardSceneReferenceMapping = z.infer<typeof storyboardSceneReferenceMappingSchema>;

export const storyboardAssetMetadataSchema = z
  .object({
    aspectRatio: storyboardAspectRatioSchema.default('16:9'),
    resolution: storyboardResolutionSchema.default('1080p'),
    modelUsed: z.string().optional(),
    generationCost: z.number().optional(),
    falRequest: z
      .object({
        status: z.string().optional(),
        queuePosition: z.number().int().nonnegative().optional(),
        updatedAt: z.string().optional()
      })
      .partial()
      .optional(),
    // Timestamps for client-side staleness detection; ISO strings
    lastVideoRequestedAt: z.string().optional(),
    lastImageRequestedAt: z.string().optional(),
    // Backend writes imageRequestedAt when queuing image jobs
    imageRequestedAt: z.string().optional(),
    // Plan-version tracking: which plan version this asset was generated against
    planVersionApplied: z.number().int().positive().optional(),
    // Plan staleness markers for UI messaging
    staleSincePlanVersion: z.number().int().positive().optional(),
    lastGeneratedPlanVersion: z.number().int().positive().optional()
  })
  .partial();
export type StoryboardAssetMetadata = z.infer<typeof storyboardAssetMetadataSchema>;

export const storyboardReferenceSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  category: storyboardReferenceCategorySchema.optional(),
  entityId: z.string().optional(),
  anchorPrompt: z.string().optional(),
  source: storyboardReferenceSourceSchema.optional(),
  status: storyboardReferenceStatusSchema.optional(),
  libraryStatus: storyboardReferenceLibraryStatusSchema.optional(),
  archivedAt: z.string().datetime().optional(),
  version: z.number().int().positive().optional(),
  versions: z.array(storyboardReferenceVersionSchema).optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  imageUrl: z.string().url().optional(),
  approval: storyboardApprovalSchema.optional(),
  staleSincePlanVersion: z.number().int().positive().optional(),
  lastGeneratedPlanVersion: z.number().int().positive().optional()
});
export type StoryboardReference = z.infer<typeof storyboardReferenceSchema>;

export const storyboardAttachmentSchema = z.object({
  storagePath: z.string(),
  storageBucket: z.string(),
  filename: z.string().optional(),
  contentType: z.string().optional(),
  mimeType: z.string().optional()
});
export type StoryboardAttachment = z.infer<typeof storyboardAttachmentSchema>;

export const storyboardAnchorSchema = z.object({
  prompt: z.string(),
  imageStoragePath: z.string().optional(),
  imageStorageBucket: z.string().optional(),
  imageUrl: z.string().url().optional(),
  status: z.enum(['pending', 'ready', 'error']).default('pending'),
  sourceType: storyboardSourceSchema.optional(),
  metadata: storyboardAssetMetadataSchema.optional(),
  staleSincePlanVersion: z.number().int().positive().optional(),
  lastGeneratedPlanVersion: z.number().int().positive().optional()
});
export type StoryboardAnchor = z.infer<typeof storyboardAnchorSchema>;

export const storyboardSceneStatusSchema = z.enum([
  'pending',
  'image_generating',
  'image_ready',
  'video_generating',
  'ready',
  'error'
]);
export type StoryboardSceneStatus = z.infer<typeof storyboardSceneStatusSchema>;

export const storyboardSceneVariationSchema = z.object({
  id: z.string(),
  summary: z.string().max(500).optional(),
  camera: z.string().max(500).optional(),
  style: z.string().max(500).optional(),
  action: z.string().max(500).optional(),
  emotion: z.string().max(200).optional(),
  dialogue: z.string().max(500).nullable().optional(),
  transition: storyboardTransitionSchema.optional(),
  durationSec: z.number().int().min(1).max(30).optional(),
  aspectRatio: storyboardAspectRatioSchema.optional(),
  stylePreset: z.string().optional(),
  colorPalette: colorPaletteEnum.optional(),
  lightingSetup: lightingSetupEnum.optional(),
  moodAtmosphere: moodAtmosphereEnum.optional(),
  prompt: z.string().max(6000).optional(),
  shotSize: shotSizeEnum.optional(),
  shotPurpose: shotPurposeEnum.optional(),
  cameraAngle: cameraAngleEnum.optional(),
  horizontalAngle: horizontalAngleEnum.optional(),
  framingGuide: framingGuideEnum.optional(),
  subjectPosition: subjectPositionEnum.optional(),
  foregroundElement: foregroundElementEnum.optional(),
  focalLength: focalLengthEnum.optional(),
  depthOfField: depthOfFieldEnum.optional(),
  focusPoint: focusPointEnum.optional(),
  locationType: locationTypeEnum.optional(),
  timeOfDay: timeOfDayEnum.optional(),
  weatherCondition: weatherConditionEnum.optional(),
  season: seasonEnum.optional(),
  subjectType: subjectTypeEnum.optional(),
  characterExpression: characterExpressionEnum.optional(),
  characterPose: characterPoseEnum.optional(),
  eyeDirection: eyeDirectionEnum.optional(),
  refIds: z.array(z.string()).optional(),
  refNotes: z.string().max(500).optional(),
  compiledPrompt: z.string().max(8000).optional(),
  compiledAt: z.string().datetime().optional(),
  compiledBy: z.string().optional(),
  cameraMovement: cameraMovementEnum.optional(),
  movementSpeed: movementSpeedEnum.optional(),
  movementMotivation: movementMotivationEnum.optional(),
  motionIntensity: motionIntensityEnum.optional(),
  subjectMotion: subjectMotionEnum.optional(),
  frameRateStyle: frameRateStyleEnum.optional(),
  customMotionNotes: z.string().max(500).optional(),
  refsUsed: z.array(z.string()).optional(),
  refIdsUsed: z.array(z.string()).optional(),
  sourceImageVariationId: z.string().optional(),
  imageStoragePath: z.string().optional(),
  imageStorageBucket: z.string().optional(),
  imageUrl: z.string().url().optional(),
  videoStoragePath: z.string().optional(),
  videoStorageBucket: z.string().optional(),
  videoUrl: z.string().url().optional(),
  status: storyboardSceneStatusSchema.optional(),
  modelUsed: z.string().optional(),
  archivedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime().optional(),
  createdBy: z.string().optional()
});
export type StoryboardSceneVariation = z.infer<typeof storyboardSceneVariationSchema>;

export const storyboardSceneSchema = z.object({
  id: z.string(),
  storyboardId: z.string(),
  sceneBundleId: z.string().optional(),
  order: z.number().int().min(0),
  durationSec: z.number().int().min(1).max(30),
  aspectRatio: storyboardAspectRatioSchema.optional(),
  stylePreset: z.string().optional(),
  colorPalette: colorPaletteEnum.optional(),
  lightingSetup: lightingSetupEnum.optional(),
  moodAtmosphere: moodAtmosphereEnum.optional(),
  summary: z.string().max(500),
  camera: z.string().max(500).optional(),
  style: z.string().max(500).optional(),
  action: z.string().max(500).optional(),
  emotion: z.string().max(200).optional(),
  dialogue: z.string().max(500).nullable().optional(),
  transition: storyboardTransitionSchema.default('jump_cut'),
  referenceHint: z.string().nullable().optional(),
  prompt: z.string().max(6000).optional(),
  videoPrompt: z.string().max(6000).optional(),
  shotSize: shotSizeEnum.optional(),
  shotPurpose: shotPurposeEnum.optional(),
  cameraAngle: cameraAngleEnum.optional(),
  horizontalAngle: horizontalAngleEnum.optional(),
  framingGuide: framingGuideEnum.optional(),
  subjectPosition: subjectPositionEnum.optional(),
  foregroundElement: foregroundElementEnum.optional(),
  focalLength: focalLengthEnum.optional(),
  depthOfField: depthOfFieldEnum.optional(),
  focusPoint: focusPointEnum.optional(),
  locationType: locationTypeEnum.optional(),
  timeOfDay: timeOfDayEnum.optional(),
  weatherCondition: weatherConditionEnum.optional(),
  season: seasonEnum.optional(),
  subjectType: subjectTypeEnum.optional(),
  characterExpression: characterExpressionEnum.optional(),
  characterPose: characterPoseEnum.optional(),
  eyeDirection: eyeDirectionEnum.optional(),
  refIds: z.array(z.string()).optional(),
  refNotes: z.string().max(500).optional(),
  compiledPrompt: z.string().max(8000).optional(),
  compiledAt: z.string().datetime().optional(),
  compiledBy: z.string().optional(),
  cameraMovement: cameraMovementEnum.optional(),
  movementSpeed: movementSpeedEnum.optional(),
  movementMotivation: movementMotivationEnum.optional(),
  motionIntensity: motionIntensityEnum.optional(),
  subjectMotion: subjectMotionEnum.optional(),
  frameRateStyle: frameRateStyleEnum.optional(),
  customMotionNotes: z.string().max(500).optional(),
  refsUsed: z.array(z.string()).optional(),
  refIdsUsed: z.array(z.string()).optional(),
  imageStoragePath: z.string().optional(),
  imageStorageBucket: z.string().optional(),
  imageUrl: z.string().url().optional(),
  videoStoragePath: z.string().optional(),
  videoStorageBucket: z.string().optional(),
  videoUrl: z.string().url().optional(),
  videoAudioMuted: z.boolean().optional(),
  status: storyboardSceneStatusSchema.default('pending'),
  sourceType: storyboardSourceSchema.optional(),
  metadata: storyboardAssetMetadataSchema.optional(),
  staleSincePlanVersion: z.number().int().positive().optional(),
  lastGeneratedPlanVersion: z.number().int().positive().optional(),
  archivedAt: z.string().datetime().optional(),
  approval: storyboardApprovalSchema.optional(),
  currentVariationId: z.string().optional(),
  variations: z.array(storyboardSceneVariationSchema).optional()
});
export type StoryboardScene = z.infer<typeof storyboardSceneSchema>;

export const storyboardAudioSegmentSchema = z.object({
  id: z.string(),
  label: z.string().max(200).optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  assetUrl: z.string().url().optional(),
  startSec: z.number().min(0),
  durationSec: z.number().min(0.1).optional(),
  trimStartSec: z.number().min(0).optional(),
  volume: z.number().min(0).max(1).optional(),
  muted: z.boolean().optional(),
  source: z.enum(['elevenlabs', 'upload', 'asset']).optional()
});
export type StoryboardAudioSegment = z.infer<typeof storyboardAudioSegmentSchema>;

export const storyboardAudioTimelineSchema = z.object({
  muted: z.boolean().default(false),
  segments: z.array(storyboardAudioSegmentSchema).default([])
});
export type StoryboardAudioTimeline = z.infer<typeof storyboardAudioTimelineSchema>;

export const storyboardTimelineSchema = z.object({
  videoMuted: z.boolean().default(false),
  voiceOver: storyboardAudioTimelineSchema.optional()
});
export type StoryboardTimeline = z.infer<typeof storyboardTimelineSchema>;

export const storyboardExportSchema = z.object({
  pdf: z.string().url().optional(),
  video: z.string().url().optional(),
  pdfStoragePath: z.string().optional(),
  pdfStorageBucket: z.string().optional(),
  videoStoragePath: z.string().optional(),
  videoStorageBucket: z.string().optional(),
  videoVersions: z
    .array(
      z.object({
        version: z.number().int(),
        storagePath: z.string(),
        storageBucket: z.string().optional(),
        signedUrl: z.string().url().optional(),
        createdAt: z.string().optional()
      })
    )
    .optional()
});
export type StoryboardExport = z.infer<typeof storyboardExportSchema>;

export const storyboardSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  orgId: z.string().optional(),
  clientId: z.string().optional(),
  projectId: z.string().optional(),
  brandKitId: z.string().optional(),
  name: z.string().max(200),
  brief: z.string().max(5000),
  briefAttachment: storyboardAttachmentSchema.optional(),
  durationSec: z.number().int().min(1).max(60),
  sceneDuration: z.number().int().min(1).max(30),
  aspectRatio: storyboardAspectRatioSchema.default('16:9'),
  stylePreset: z.string().optional(),
  mode: storyboardModeSchema.default('detailed'),
  approvalMode: storyboardApprovalModeSchema.default('manual'),
  status: storyboardStatusSchema,
  anchorPrompt: z.string().optional(),
  anchor: storyboardAnchorSchema.optional(),
  references: z.array(storyboardReferenceSchema).optional(),
  referencePlan: storyboardReferencePlanSchema.optional(),
  sceneReferencePlan: z.array(storyboardSceneReferenceMappingSchema).optional(),
  scenes: z.array(storyboardSceneSchema),
  archivedScenes: z.array(storyboardSceneSchema).optional(),
  generationMode: storyboardGenerationModeSchema.default('auto'),
  visualBible: visualBibleSchema.optional(),
  // NEW: Story Bible for global visual consistency
  storyBible: storyBibleSchema.optional(),
  // NEW: Enhanced scenes with full director-level specs (parallel to legacy scenes)
  enhancedScenes: z.array(enhancedSceneShotSchema).optional(),
  // Plan version tracking
  planVersion: z.number().int().positive().default(1),
  planVersions: z
    .array(
      z.object({
        version: z.number().int().positive(),
        createdAt: z.string().datetime(),
        createdBy: z.string().optional(),
        storyBible: storyBibleSchema.optional(),
        scenes: z
          .array(
            z.object({
              id: z.string(),
              order: z.number().int().min(0),
              summary: z.string().max(500),
              camera: z.string().max(500).optional(),
              style: z.string().max(500).optional(),
              action: z.string().max(500).optional(),
              emotion: z.string().max(200).optional(),
              dialogue: z.string().max(500).nullable().optional(),
              transition: storyboardTransitionSchema.optional(),
              durationSec: z.number().int().min(1).max(30).optional()
            })
          )
          .optional(),
        anchorPrompt: z.string().optional(),
        referencePlan: storyboardReferencePlanSchema.optional(),
        sceneReferencePlan: z.array(storyboardSceneReferenceMappingSchema).optional()
      })
    )
    .optional(),
  exportUrls: storyboardExportSchema.optional(),
  timeline: storyboardTimelineSchema.optional(),
  totalCreditsUsed: z.number().default(0),
  estimatedCreditsRemaining: z.number().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type Storyboard = z.infer<typeof storyboardSchema>;
